import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { ZoneCategory, CreateZoneCategoryDto, UpdateZoneCategoryDto } from '../../models/ZoneCategory';
import * as zoneCategoryService from '../../services/zoneCategoryService';
import * as zoneService from '../../services/newZoneService';
import { useAuth } from '../../hooks/useAuth';
import { useSnackbar } from 'notistack';

interface CategoryManagementModalProps {
  open: boolean;
  onClose: () => void;
  onCategoriesUpdated: () => void;
}

const CATEGORY_ICONS = [
  'fas fa-home', 'fas fa-utensils', 'fas fa-umbrella-beach', 'fas fa-music', 'fas fa-cocktail',
  'fas fa-spa', 'fas fa-swimming-pool', 'fas fa-dumbbell', 'fas fa-gamepad', 'fas fa-coffee',
  'fas fa-store', 'fas fa-concierge-bell', 'fas fa-bed', 'fas fa-car', 'fas fa-plane',
  'fas fa-wifi', 'fas fa-phone', 'fas fa-desktop', 'fas fa-tv', 'fas fa-camera',
  'fas fa-shield-alt', 'fas fa-heart', 'fas fa-star', 'fas fa-leaf', 'fas fa-sun'
];

const CATEGORY_COLORS = [
  '#4294e3', '#ed6c02', '#0288d1', '#7b1fa2', '#d32f2f',
  '#388e3c', '#f57c00', '#5d4037', '#616161', '#e91e63',
  '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#00bcd4',
  '#009688', '#4caf50', '#8bc34a', '#cddc39', '#ffeb3b'
];

export const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({
  open,
  onClose,
  onCategoriesUpdated
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [categories, setCategories] = useState<ZoneCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ZoneCategory | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [zones, setZones] = useState<any[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; category: ZoneCategory | null }>({ open: false, category: null });

  const [formData, setFormData] = useState<CreateZoneCategoryDto | UpdateZoneCategoryDto>({
    name: '',
    icon: CATEGORY_ICONS[0],
    color: CATEGORY_COLORS[0],
    description: '',
    hotelId: user?.hotelId || 0
  });
  const [formLoading, setFormLoading] = useState(false);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await zoneCategoryService.getZoneCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      enqueueSnackbar('Kategoriler yüklenemedi', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadZones = async () => {
    try {
      const data = await zoneService.getZones();
      setZones(data);
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  };

  useEffect(() => {
    if (open) {
      loadCategories();
      loadZones();
    }
  }, [open]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      icon: CATEGORY_ICONS[0],
      color: CATEGORY_COLORS[0],
      description: '',
      hotelId: user?.hotelId || 0
    });
  }, [user?.hotelId]);

  const handleClose = useCallback(() => {
    setShowCreateForm(false);
    setEditingCategory(null);
    setTabValue(0);
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleCreateCategory = useCallback(async () => {
    try {
      setFormLoading(true);
      await zoneCategoryService.createZoneCategory(formData as CreateZoneCategoryDto);
      enqueueSnackbar('Kategori oluşturuldu', { variant: 'success' });
      setShowCreateForm(false);
      resetForm();
      await loadCategories();
      onCategoriesUpdated();
    } catch (error) {
      console.error('Error creating category:', error);
      enqueueSnackbar('Kategori oluşturulamadı', { variant: 'error' });
    } finally {
      setFormLoading(false);
    }
  }, [formData, resetForm, onCategoriesUpdated, enqueueSnackbar]);

  const handleUpdateCategory = useCallback(async () => {
    if (!editingCategory) return;
    try {
      setFormLoading(true);
      await zoneCategoryService.updateZoneCategory(editingCategory.id, formData as UpdateZoneCategoryDto);
      enqueueSnackbar('Kategori güncellendi', { variant: 'success' });
      setEditingCategory(null);
      setShowCreateForm(false);
      resetForm();
      await loadCategories();
      onCategoriesUpdated();
    } catch (error) {
      console.error('Error updating category:', error);
      enqueueSnackbar('Kategori güncellenemedi', { variant: 'error' });
    } finally {
      setFormLoading(false);
    }
  }, [editingCategory, formData, resetForm, onCategoriesUpdated, enqueueSnackbar]);

  const handleDeleteCategory = async (category: ZoneCategory) => {
    try {
      await zoneCategoryService.deleteZoneCategory(category.id);
      enqueueSnackbar('Kategori silindi', { variant: 'success' });
      setDeleteConfirm({ open: false, category: null });
      await loadCategories();
      onCategoriesUpdated();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      enqueueSnackbar(error.response?.data?.message || 'Kategori silinemedi', { variant: 'error' });
    }
  };

  const handleEditClick = (category: ZoneCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon,
      color: category.color,
      description: category.description || ''
    });
    setShowCreateForm(true);
  };

  const handleFormSubmit = () => {
    if (editingCategory) {
      handleUpdateCategory();
    } else {
      handleCreateCategory();
    }
  };

  const handleZoneCategoryAssignment = async (zoneId: number, categoryId: number | null) => {
    try {
      const zone = zones.find(z => z.id === zoneId);
      if (!zone) return;
      const updateData = {
        name: zone.name,
        deviceName: zone.deviceName,
        outputChannel: zone.outputChannel,
        musicFolderPath: zone.musicFolderPath,
        musicFolderPaths: zone.musicFolderPaths || [],
        hotelId: zone.hotelId,
        categoryId: categoryId
      };
      await zoneService.updateZone(zoneId, updateData);
      enqueueSnackbar('Zone kategorisi güncellendi', { variant: 'success' });
      await loadZones();
      await loadCategories();
      onCategoriesUpdated();
    } catch (error) {
      console.error('Error updating zone category:', error);
      enqueueSnackbar('Zone kategorisi güncellenemedi', { variant: 'error' });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '85vh'
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0',
          py: 2, px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              backgroundColor: 'rgba(66, 148, 227, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <CategoryRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e' }}>
              Kategori Yönetimi
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small"
            sx={{ width: 32, height: 32, color: '#999', '&:hover': { color: '#666', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ px: 3, pt: 1, borderBottom: '1px solid #f0f0f0' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                minHeight: 40, py: 0, color: '#999',
                '&.Mui-selected': { color: '#4294e3' }
              },
              '& .MuiTabs-indicator': { backgroundColor: '#4294e3', height: 2.5, borderRadius: '2px 2px 0 0' }
            }}
          >
            <Tab label="Kategoriler" />
            <Tab label="Zone Atamaları" />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#4294e3' }} />
            </Box>
          ) : (
            <>
              {/* Tab 0: Category Management */}
              {tabValue === 0 && (
                <>
                  {/* Add button */}
                  {!showCreateForm && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={() => { setEditingCategory(null); resetForm(); setShowCreateForm(true); }}
                        sx={{
                          textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                          borderRadius: '10px', px: 2, py: 0.7,
                          backgroundColor: '#4294e3', color: '#fff',
                          '&:hover': { backgroundColor: '#3680c9' }
                        }}
                      >
                        Kategori Ekle
                      </Button>
                    </Box>
                  )}

                  {/* Create/Edit Form */}
                  {showCreateForm && (
                    <Box sx={{
                      p: 2.5, mb: 2, borderRadius: '14px',
                      border: '1px solid #f0f0f0', backgroundColor: '#fafafa'
                    }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e', mb: 2 }}>
                        {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" label="Kategori Adı"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth size="small" label="Açıklama"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            multiline rows={2}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Simge</InputLabel>
                            <Select
                              value={formData.icon}
                              onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                              label="Simge"
                              sx={{ borderRadius: '10px' }}
                              renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <i className={value} style={{ fontSize: '16px' }} />
                                  <span style={{ fontSize: '0.8rem' }}>Seçildi</span>
                                </Box>
                              )}
                              MenuProps={{ PaperProps: { style: { maxHeight: 250, borderRadius: 12 } } }}
                            >
                              {CATEGORY_ICONS.map((icon) => (
                                <MenuItem key={icon} value={icon}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 0.5 }}>
                                    <i className={icon} style={{ fontSize: '18px' }} />
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Renk</InputLabel>
                            <Select
                              value={formData.color}
                              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                              label="Renk"
                              sx={{ borderRadius: '10px' }}
                              renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 16, height: 16, backgroundColor: value, borderRadius: '50%' }} />
                                  <span style={{ fontSize: '0.8rem' }}>{value}</span>
                                </Box>
                              )}
                            >
                              {CATEGORY_COLORS.map((color) => (
                                <MenuItem key={color} value={color}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 18, height: 18, backgroundColor: color, borderRadius: '50%' }} />
                                    <span style={{ fontSize: '0.8rem' }}>{color}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                          onClick={() => { setShowCreateForm(false); setEditingCategory(null); resetForm(); }}
                          sx={{
                            textTransform: 'none', fontWeight: 500, fontSize: '0.8rem',
                            borderRadius: '10px', px: 2, color: '#666',
                            backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#eee' }
                          }}
                        >
                          Vazgeç
                        </Button>
                        <Button
                          onClick={handleFormSubmit}
                          disabled={!formData.name.trim() || formLoading}
                          sx={{
                            textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                            borderRadius: '10px', px: 2.5,
                            backgroundColor: '#4294e3', color: '#fff',
                            '&:hover': { backgroundColor: '#3680c9' },
                            '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#999' }
                          }}
                        >
                          {formLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> :
                            editingCategory ? 'Güncelle' : 'Oluştur'}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Category List */}
                  {categories.length === 0 ? (
                    <Box sx={{
                      py: 5, textAlign: 'center',
                      borderRadius: '14px', border: '2px dashed #e8e8e8',
                      backgroundColor: '#fafafa'
                    }}>
                      <CategoryRoundedIcon sx={{ fontSize: 36, color: '#ccc', mb: 1.5 }} />
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#888', mb: 0.5 }}>
                        Kategori Yok
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#aaa' }}>
                        Henüz kategori oluşturulmamış
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {categories.map((category) => (
                        <Box
                          key={category.id}
                          sx={{
                            p: 2, borderRadius: '12px',
                            border: '1px solid #f0f0f0',
                            backgroundColor: '#fff',
                            transition: 'all 0.2s',
                            '&:hover': { backgroundColor: '#fafafa', borderColor: '#e0e0e0' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                              <Box sx={{
                                width: 32, height: 32, borderRadius: '8px',
                                backgroundColor: `${category.color}15`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                              }}>
                                <i className={category.icon} style={{ fontSize: '14px', color: category.color }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                  <Typography sx={{
                                    fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                  }}>
                                    {category.name}
                                  </Typography>
                                  <Chip
                                    label={`${category.zoneCount} zone`}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.6rem', fontWeight: 600,
                                      backgroundColor: `${category.color}15`, color: category.color,
                                      borderRadius: '6px'
                                    }}
                                  />
                                </Box>
                                {category.description && (
                                  <Typography sx={{ fontSize: '0.7rem', color: '#999', mt: 0.2 }}>
                                    {category.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              <IconButton size="small" onClick={() => handleEditClick(category)}
                                sx={{ color: '#888', '&:hover': { color: '#4294e3' } }}>
                                <EditRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <Tooltip title={category.zoneCount > 0 ? 'Zone atanmış kategoriler silinemez' : ''}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={category.zoneCount > 0}
                                    onClick={() => setDeleteConfirm({ open: true, category })}
                                    sx={{ color: '#bbb', '&:hover': { color: '#c62828' } }}
                                  >
                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}

              {/* Tab 1: Zone Assignments */}
              {tabValue === 1 && (
                <Box>
                  {categories.length === 0 ? (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.85rem', color: '#999' }}>
                        Henüz kategori yok. Önce kategori oluşturun.
                      </Typography>
                    </Box>
                  ) : (
                    categories.map((category) => {
                      const categoryZones = zones.filter(zone => zone.categoryId === category.id);
                      const availableZones = zones.filter(zone => !zone.categoryId);

                      return (
                        <Box key={category.id} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: '7px',
                              backgroundColor: `${category.color}15`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              <i className={category.icon} style={{ fontSize: '12px', color: category.color }} />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e' }}>
                              {category.name}
                            </Typography>
                            <Chip
                              label={`${categoryZones.length} zone`}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.6rem', fontWeight: 600,
                                backgroundColor: `${category.color}15`, color: category.color,
                                borderRadius: '6px'
                              }}
                            />
                          </Box>

                          {/* Assigned zones */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5, pl: 1 }}>
                            {categoryZones.length > 0 ? categoryZones.map((zone) => (
                              <Chip
                                key={zone.id}
                                label={zone.name}
                                size="small"
                                onDelete={() => handleZoneCategoryAssignment(zone.id, null)}
                                sx={{
                                  height: 26, fontSize: '0.75rem', fontWeight: 500,
                                  borderRadius: '8px',
                                  backgroundColor: `${category.color}12`,
                                  color: category.color,
                                  border: `1px solid ${category.color}30`,
                                  '& .MuiChip-deleteIcon': { color: `${category.color}80`, fontSize: 16 }
                                }}
                              />
                            )) : (
                              <Typography sx={{ fontSize: '0.75rem', color: '#bbb', fontStyle: 'italic' }}>
                                Henüz zone atanmamış
                              </Typography>
                            )}
                          </Box>

                          {/* Available zones to add */}
                          {availableZones.length > 0 && (
                            <Box sx={{ pl: 1 }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#999', mb: 0.5 }}>
                                Atanmamış zone'lar:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {availableZones.map((zone) => (
                                  <Chip
                                    key={zone.id}
                                    label={zone.name}
                                    size="small"
                                    onClick={() => handleZoneCategoryAssignment(zone.id, category.id)}
                                    sx={{
                                      height: 24, fontSize: '0.7rem', fontWeight: 500,
                                      borderRadius: '7px', cursor: 'pointer',
                                      backgroundColor: '#f5f5f5', color: '#666',
                                      border: '1px dashed #ddd',
                                      '&:hover': { backgroundColor: `${category.color}10`, borderColor: category.color, color: category.color }
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  )}

                  {zones.filter(zone => !zone.categoryId).length === 0 && categories.length > 0 && (
                    <Box sx={{
                      mt: 2, p: 2, borderRadius: '10px',
                      backgroundColor: 'rgba(56, 142, 60, 0.05)',
                      border: '1px solid rgba(56, 142, 60, 0.15)',
                      textAlign: 'center'
                    }}>
                      <CheckRoundedIcon sx={{ fontSize: 20, color: '#388e3c', mb: 0.5 }} />
                      <Typography sx={{ fontSize: '0.8rem', color: '#388e3c', fontWeight: 500 }}>
                        Tüm zone'lar kategorilere atanmış
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, category: null })}
        PaperProps={{ sx: { borderRadius: '16px', maxWidth: 380, mx: 'auto', overflow: 'hidden' } }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 2,
            backgroundColor: 'rgba(229, 57, 53, 0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 24, color: '#c62828' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#1a1a2e', mb: 0.5 }}>
            Kategoriyi Sil
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 2.5, lineHeight: 1.5 }}>
            <strong style={{ color: '#1a1a2e' }}>"{deleteConfirm.category?.name}"</strong> kategorisini silmek istiyor musunuz?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              onClick={() => setDeleteConfirm({ open: false, category: null })}
              sx={{
                textTransform: 'none', fontWeight: 500, borderRadius: '10px',
                color: '#666', backgroundColor: '#f5f5f5', py: 1,
                '&:hover': { backgroundColor: '#eee' }
              }}
            >
              Vazgeç
            </Button>
            <Button
              fullWidth
              onClick={() => deleteConfirm.category && handleDeleteCategory(deleteConfirm.category)}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                backgroundColor: '#c62828', color: '#fff', py: 1,
                '&:hover': { backgroundColor: '#b71c1c' }
              }}
            >
              Sil
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
